import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import SectionTabs, { type SectionTab } from "../../shared/section-tabs/SectionTabs";
import RankInsignia from "../../shared/rating/RankInsignia";
import { tierForValue } from "../../shared/rating/tiers";
import { fetchBotLeaderboard, saveBotSettings, purgeOldBotRows, type BotLeaderboard, type BotLevelId, type BotRow, type LadderRow } from "../../shared/admin-bots";
import { CROWD_LADDER, crowdLevelRosters, crowdLevelNumber } from "core/src/ai/crowd.js";
import { LOAD_TESTS } from "core/src/ai/load-tests.js";
import { BOT_TRAITS } from "core/src/ai/bot-traits.js";
import Icon from "../setup/Icon";
import PowerUpValuesTab from "./PowerUpValuesTab";
import styles from "./BotLeaderboardPage.module.scss";

// Bot management (admin-only): the bots' own leaderboard, the place they get tuned. Every bot is
// here, played or not (an unplayed one reads Unranked). One tab per crowd level
// (core crowd.js switches bot features off as the roster grows) plus All bots.
// Every level has its own cast of named bots (core bot-cast.js) and a bot lives
// on one level only, so a level is a clean ladder: the same features on for
// every match a number was earned in. Click a bot for its record and to change
// its settings. Never linked from anything a player sees: the bots are a secret.

interface TraitMeta { key: string; label: string; short?: string; min: number; max: number; step: number; unset: number; kind: "skill" | "personality"; blurb: string }
const TRAITS = BOT_TRAITS as TraitMeta[];
function traitMeta(key: string): TraitMeta {
  return TRAITS.find((t) => t.key === key) || { key, label: key, min: -1, max: 1, step: 0.05, unset: 0, kind: "personality", blurb: "" };
}

// "1v1", "3–8", "25+": the rosters a level covers (the ladder counts players, so
// the 1v1 level is rosters up to 2).
function rosterLabel(min: number, max: number | null): string {
  if (max != null && max <= 2) return "1v1";
  return max == null || max === Infinity ? `${min}+` : `${min}–${max}`;
}
const LEVELS: { level: number; roster: string }[] = CROWD_LADDER.map((row: { level: number }) => {
  const r = crowdLevelRosters(row.level);
  return { level: row.level, roster: rosterLabel(r.min, r.max) };
});
// One tab per crowd level, straight from core's ladder, so a re-levelled ladder
// re-labels the page on its own.
const TABS: SectionTab<string>[] = [
  { id: "all", label: "All bots" },
  // (levels show as 1 to 5; the tab ids, like the code and the rating ids, count from 0)
  ...LEVELS.map((l) => ({ id: String(l.level), label: `Lvl ${crowdLevelNumber(l.level)} · ${l.roster}` })),
  { id: "powerups", label: "Power-ups" },
];

const BOX_NAMES: Record<string, string> = { small: "Duel", medium: "Brawl", large: "Royale" };

type SortKey = "rating" | "games" | "winPct" | "placePct" | "poolElo" | `trait:${string}`;

const pct = (n: number, d: number): number | null => (d > 0 ? Math.round((1000 * n) / d) / 10 : null);
// A table cell with nothing to show stays empty; the opened panel uses a dash.
const cell = (v: number | null | undefined, suffix = ""): string => (v == null ? "" : v + suffix);
const fmt = (v: number | null | undefined, suffix = ""): string => (v == null ? "–" : v + suffix);
const played = (b: BotRow) => b.games > 0 && b.rating != null;

// What a bot actually plays with for a trait. The "how good" dials (planning
// depth, planning breadth, precision) always have a value: a bot that doesn't
// set one gets the planner's default (breadth 1 = all 9 options), so the table
// shows that. An unset personality trait is simply absent: the cell stays empty.
function traitValueOf(b: BotRow, key: string): number | undefined {
  if (!b.traits) return undefined;
  const v = b.traits[key];
  if (v != null) return v;
  const meta = TRAITS.find((t) => t.key === key);
  return meta && meta.kind === "skill" ? meta.unset : undefined;
}

function sortValue(b: BotRow, key: SortKey): number | null {
  if (key.startsWith("trait:")) return traitValueOf(b, key.slice(6)) ?? null;
  if (key === "winPct") return pct(b.wins, b.games);
  return (b as any)[key] ?? null;
}

// Pearson correlation of a trait with the live rating, over bots with enough
// games to mean something. null when there's too little to go on.
const CORR_MIN_GAMES = 5;
function correlation(bots: BotRow[], trait: string): number | null {
  const pts = bots.filter((b) => b.games >= CORR_MIN_GAMES && b.rating != null && traitValueOf(b, trait) != null).map((b) => [traitValueOf(b, trait)!, b.rating!] as const);
  if (pts.length < 8) return null;
  const mx = pts.reduce((s, p) => s + p[0], 0) / pts.length, my = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  let sxy = 0, sxx = 0, syy = 0;
  for (const [x, y] of pts) { sxy += (x - mx) * (y - my); sxx += (x - mx) ** 2; syy += (y - my) ** 2; }
  return sxx > 0 && syy > 0 ? sxy / Math.sqrt(sxx * syy) : null;
}

// Which trait columns a level's settings make meaningless, and why. A level
// with personalities off never scores a path by caution, aggression, greed or
// space, so those values are just stored numbers there: the table must say so
// instead of showing bars that look like they matter. (The one rule today; add
// the next here and the header, the cells, the level headings and the editor
// all follow.)
function ignoredTraits(row: LadderRow | null | undefined): { keys: Set<string>; why: string } | null {
  if (!row || row.personalities) return null;
  return { keys: new Set(TRAITS.filter((t) => t.kind === "personality").map((t) => t.key)), why: "Personalities are off on this level" };
}
const listWords = (w: string[]) => (w.length > 1 ? w.slice(0, -1).join(", ") + " and " + w[w.length - 1] : w.join(""));

// A number in its rank's colour, the rank's badge to its right (so a column of
// numbers stays right-aligned and the badges line up after it).
function RatedNumber({ value, strong }: { value: number | null; strong?: boolean }) {
  if (value == null) return <span className={styles.unranked}>Unranked</span>;
  return (
    <span className={styles.rated}>
      <span className={strong ? styles.ratedStrong : styles.ratedNum} style={{ color: tierForValue(value).color }}>{value}</span>
      <RankInsignia value={value} size={strong ? 26 : 24} />
    </span>
  );
}

function TraitBar({ meta, value, off, base }: { meta: TraitMeta; value: number | undefined; off?: boolean; base?: number }) {
  if (value == null) return null; // nothing set: the cell stays empty
  const signed = meta.min < 0;
  const span = meta.max - meta.min;
  // signed traits grow from the centre line; 0..1 traits from the left
  const left = signed ? (Math.min(0, value) - meta.min) / span : 0;
  const width = signed ? Math.abs(value) / span : (value - meta.min) / span;
  const changed = base != null && base !== value;
  return (
    <span className={`${styles.traitBar} ${off ? styles.traitOff : ""}`} title={`${meta.label} ${value}${changed ? ` (default ${base})` : ""}${off ? ". Does nothing on this level: personalities are off" : ""}`}>
      {signed && <i className={styles.traitZero} />}
      <b className={value < 0 ? styles.traitNeg : meta.kind === "skill" ? styles.traitSkill : styles.traitPos} style={{ left: `${left * 100}%`, width: `${Math.max(0.03, width) * 100}%` }} />
      {changed && <u className={styles.traitTuned} />}
    </span>
  );
}

// ---- the level's settings, each with a proper tooltip -----------------------

interface KnobDef { label: string; about: string; show: (r: LadderRow) => string; on?: (r: LadderRow) => boolean }
const lim = (v: number | null) => (v == null ? "no limit" : String(v));
const onOff = (v: boolean) => (v ? "on" : "off");
const KNOBS: KnobDef[] = [
  { label: "Personalities", show: (r) => onOff(r.personalities), on: (r) => r.personalities, about: "Scores every candidate path by the bot's caution, aggression, greed and space. Off: those four traits do nothing, and only planning and precision tell two bots apart. The most expensive part of a bot's thinking." },
  { label: "Opponent prediction", show: (r) => onOff(r.threats), on: (r) => r.threats, about: "Projects where nearby opponents are heading and treats those trails-to-be as probable obstacles, so a bot doesn't steer into a gap someone else is about to close." },
  { label: "Own room map", show: (r) => onOff(r.ownField), on: (r) => r.ownField, about: "Each bot measures the open room it can reach from its own head. Off: every bot reads one shared room map for the whole board, which is cheaper but blunter." },
  { label: "Turn fan", show: (r) => String(r.fan), about: "How many turn options the planner compares each time it thinks (straight, gentle and hard turns each way). The biggest single lever on how long a bot survives." },
  { label: "Lookahead", show: (r) => `×${r.lookaheadScale}`, about: "A scale on how far ahead every bot simulates its path (a bot's own planning trait sets the base, 1.8 to 2.7 seconds). Below 1 = shorter sight." },
  { label: "Replan pace", show: (r) => `×${r.replanScale}`, about: "A scale on the time between a bot's plans (its precision trait sets the base). Above 1 = it thinks less often, so it reacts a little later." },
  { label: "Re-evals / tick", show: (r) => lim(r.maxReevalsPerTick), about: "How many bots may re-check their current plan in one server tick, across the whole match. A budget that spreads the thinking over ticks so a busy moment can't stall the host." },
  { label: "Full replans / tick", show: (r) => lim(r.maxFullReplansPerTick), about: "How many bots may build a whole new plan in one server tick, across the whole match. A bot in danger that misses its turn falls back to a quick emergency menu." },
  { label: "Board refresh", show: (r) => `${r.boardRefresh}s`, about: "How often the shared picture of the board (open room, clearance) is rebuilt. Longer = bots act on a slightly older picture." },
  { label: "Host step", show: (r) => `${r.tickMs}ms`, about: "The simulation step of the whole match. 15 ms is the normal game; 20 ms is coarser and cheaper, and is what lets a 25+ match run in real time." },
];

function Knob({ def, row, base }: { def: KnobDef; row: LadderRow; base: LadderRow }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [tip, setTip] = useState<{ left: number; top: number } | null>(null);
  const value = def.show(row), baseValue = def.show(base);
  const changed = value !== baseValue;
  const isOn = def.on ? def.on(row) : undefined;
  const cls = [styles.knob, isOn === true ? styles.knobOn : "", isOn === false ? styles.knobOff : "", changed ? styles.knobChanged : ""].filter(Boolean).join(" ");
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const width = 320;
    setTip({ left: Math.max(12, Math.min(window.innerWidth - width - 12, r.left)), top: r.bottom + 8 });
  };
  return (
    <span ref={ref} className={cls} tabIndex={0} onMouseEnter={show} onMouseLeave={() => setTip(null)} onFocus={show} onBlur={() => setTip(null)}>
      <em>{def.label}</em>
      <strong>{value}</strong>
      {tip && createPortal(
        <div className={styles.knobTip} style={{ left: tip.left, top: tip.top }} role="tooltip">
          <div className={styles.knobTipTitle}>{def.label}</div>
          <p>{def.about}</p>
          <dl>
            <div className={changed ? styles.knobTipChanged : undefined}><dt>Level {crowdLevelNumber(row.level)}</dt><dd>{value}</dd></div>
            <div><dt>Level {crowdLevelNumber(0)} <span>the full bot</span></dt><dd>{baseValue}</dd></div>
          </dl>
        </div>,
        document.body,
      )}
    </span>
  );
}

function LevelCard({ row, base, boxes }: { row: LadderRow; base: LadderRow; boxes: { base: string; capacity: number }[] }) {
  const navigate = useNavigate();
  const feeds = boxes.length ? boxes.map((b) => `${BOX_NAMES[b.base] || b.base} of ${b.capacity}`).join(", ") : "no rated box lands here yet, so nothing is ranked";
  const loadTest = LOAD_TESTS.find((t: { level: number }) => t.level === row.level) as { base: string; capacity: number } | undefined;
  const roster = rosterLabel(row.minRoster, row.maxRoster);
  return (
    <section className={styles.levelCard}>
      <div className={styles.levelHead}>
        <h2>Level {crowdLevelNumber(row.level)}</h2>
        <span className={styles.levelRoster}>{roster === "1v1" ? "1v1" : `${roster} players`}</span>
        <span className={styles.levelFeeds}>Fed by: {feeds}</span>
        {loadTest && (
          <button type="button" className={styles.loadTest} onClick={() => navigate(`/game/${loadTest.base}?netstats=1`)} title="An unrated online match at this level's biggest roster, with the netstats overlay: can the host keep pace?">
            <Icon name="gauge" size={14} />
            <span>Load test</span>
            <em>{loadTest.capacity} seats</em>
          </button>
        )}
      </div>
      <div className={styles.knobs}>
        {KNOBS.map((k) => <Knob key={k.label} def={k} row={row} base={base} />)}
      </div>
      <p className={styles.levelNote}>{row.level > 0 ? `Orange outline = differs from level ${crowdLevelNumber(0)}. ` : ""}Hover a setting for what it does.</p>
    </section>
  );
}

// ---- a bot, opened: its record, its settings, its change log ----------------

function BotEditor({ bot, personalitiesOff, onSaved }: { bot: BotRow; personalitiesOff: boolean; onSaved: () => void }) {
  const editable = bot.inPool && bot.level != null && !!bot.baseTraits;
  const keys = useMemo(() => {
    const have = new Set(Object.keys({ ...(bot.baseTraits || {}), ...(bot.traits || {}) }));
    // every tunable trait is offered, even one this bot's default leaves unset
    return [...TRAITS.map((t) => t.key), ...[...have].filter((k) => !TRAITS.some((t) => t.key === k))];
  }, [bot]);
  const [draft, setDraft] = useState<Record<string, number>>(() => ({ ...(bot.traits || {}) }));
  const [resetRating, setResetRating] = useState(bot.games > 0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const saved = bot.traits || {};
  const base = bot.baseTraits || {};
  const dirty = keys.some((k) => (draft[k] ?? null) !== (saved[k] ?? null));
  const atDefault = keys.every((k) => (draft[k] ?? null) === (base[k] ?? null));

  async function save(traits: Record<string, number> | null) {
    setBusy(true);
    setMsg(null);
    const res = await saveBotSettings({ agentId: bot.agentId, traits, resetRating: resetRating && bot.games > 0, note });
    setBusy(false);
    if (res.error) {
      setMsg({ ok: false, text: res.error === "not_signed_in" ? "Sign in with an admin account first." : res.error === "not_admin" ? "This account isn't on the admin list." : `Couldn't save (${res.error}).` });
      return;
    }
    setMsg({ ok: true, text: "Saved. It plays with these settings from its next match" + (res.ratingReset ? ", and its rating starts over." : ".") });
    setNote("");
    onSaved();
  }

  return (
    <div className={styles.detail}>
      <div className={styles.detailTraits}>
        <h3>Settings {bot.tuned && <span className={styles.tunedTag}>tuned · v{bot.version}</span>}</h3>
        {!bot.traits && <p className={styles.dim}>This id is no longer in a cast, so it has no settings. The rating row is old data.</p>}
        {bot.traits && keys.map((k) => {
          const meta = traitMeta(k);
          const off = personalitiesOff && meta.kind === "personality";
          const v = draft[k];
          const changedFromSaved = (v ?? null) !== (saved[k] ?? null);
          const differsFromDefault = (v ?? null) !== (base[k] ?? null);
          return (
            <div key={k} className={[styles.setting, off ? styles.settingOff : "", changedFromSaved ? styles.settingDirty : ""].filter(Boolean).join(" ")}>
              <span className={styles.settingName}>{meta.label}</span>
              {editable ? (
                <input
                  className={styles.settingSlider}
                  type="range"
                  min={meta.min}
                  max={meta.max}
                  step={meta.step}
                  value={v ?? meta.unset}
                  aria-label={meta.label}
                  onChange={(e) => setDraft((d) => ({ ...d, [k]: Number(e.target.value) }))}
                />
              ) : <TraitBar meta={meta} value={v} off={off} />}
              <span className={styles.settingValue} title={v == null ? `Not set on this bot, so the planner uses ${meta.unset}` : undefined}>{(v ?? meta.unset).toFixed(2)}{v == null ? "*" : ""}</span>
              <span className={styles.settingDefault}>
                {differsFromDefault && editable ? (
                  <button type="button" onClick={() => setDraft((d) => { const n = { ...d }; if (base[k] == null) delete n[k]; else n[k] = base[k]; return n; })} title="Put this one back on its default">
                    default {base[k] == null ? meta.unset + "*" : base[k]} ↺
                  </button>
                ) : <span>default</span>}
              </span>
              <span className={styles.settingBlurb}>{off ? "Ignored on this level (personalities off). " : ""}{meta.blurb}</span>
            </div>
          );
        })}
        {editable && (
          <div className={styles.editorFoot}>
            {bot.games > 0 && (
              <label className={styles.resetRow}>
                <input type="checkbox" checked={resetRating} onChange={(e) => setResetRating(e.target.checked)} />
                <span>Start its rating over <em>(recommended: {bot.rating} over {bot.games} games was earned by the old settings)</em></span>
              </label>
            )}
            <input className={styles.noteInput} type="text" maxLength={200} value={note} placeholder="Note for the change log, e.g. “less wall-hugging”" onChange={(e) => setNote(e.target.value)} />
            <div className={styles.editorButtons}>
              <button type="button" className="app-btn app-btn-primary app-btn-sm" disabled={!dirty || busy} onClick={() => save(atDefault ? null : draft)}>{busy ? "Saving…" : "Save settings"}</button>
              <button type="button" className="app-btn app-btn-ghost app-btn-sm" disabled={!dirty || busy} onClick={() => setDraft({ ...saved })}>Undo edits</button>
              {bot.tuned && <button type="button" className="app-btn app-btn-ghost app-btn-sm" disabled={busy} onClick={() => save(null)}>Back to default</button>}
              {/* Feel it: a duel against this bot in the trait lab, with the sliders as
                  they stand right now (saved or not), in a new tab so the edit stays open. */}
              <a
                className={`app-btn app-btn-ghost app-btn-sm ${styles.duelBtn}`}
                href={`/debug/bots?vs=${encodeURIComponent(JSON.stringify({ name: bot.name || bot.poolId, traits: draft }))}`}
                target="_blank"
                rel="noreferrer"
                title={dirty ? "Play against it with your unsaved edits" : "Play against it as it is now"}
              >
                Play 1v1{dirty ? " (with these edits)" : ""} ▸
              </a>
            </div>
            {msg && <p className={msg.ok ? styles.msgOk : styles.msgBad}>{msg.text}</p>}
          </div>
        )}
      </div>

      <div className={styles.detailSide}>
        <dl className={styles.detailStats}>
          <h3>Record{bot.level != null ? ` on level ${crowdLevelNumber(bot.level)}` : ""}</h3>
          <div><dt>Rating</dt><dd>{played(bot) ? <>{bot.rating} <span className={styles.dim}>(peak {fmt(bot.peak)})</span></> : <span className={styles.unranked}>Unranked: no games yet</span>}</dd></div>
          <div><dt>Games</dt><dd>{bot.games} <span className={styles.dim}>({bot.wins} wins, {bot.podiums} top 3, best place {fmt(bot.bestPlace)})</span></dd></div>
          <div><dt>Win rate</dt><dd>{fmt(pct(bot.wins, bot.games), "%")} <span className={styles.dim}>· top 3 in {fmt(pct(bot.podiums, bot.games), "%")}</span></dd></div>
          <div><dt>Field beaten</dt><dd>{fmt(bot.placePct, "%")} <span className={styles.dim}>on average (1st = 100, last = 0)</span></dd></div>
          <div><dt>Certainty</dt><dd>{bot.mu == null ? "no games yet" : `μ ${bot.mu.toFixed(1)} ± ${bot.sigma!.toFixed(1)}`} <span className={styles.dim}>{bot.sigma == null ? "" : bot.sigma > 6 ? "still settling" : bot.sigma > 3.5 ? "getting there" : "settled"}</span></dd></div>
          <div><dt>Pool Elo</dt><dd>{fmt(bot.poolElo)} <span className={styles.dim}>measured offline for its default settings ({fmt(bot.poolRounds)} rounds). Seating picks bots by this.</span></dd></div>
          <div><dt>Last played</dt><dd>{bot.updatedAt ? new Date(bot.updatedAt).toLocaleString() : "not yet"}</dd></div>
          <div><dt>Rating id</dt><dd><span className={styles.mono}>{bot.agentId}</span> <span className={styles.dim}>(ids count levels from 0)</span></dd></div>
        </dl>
        {bot.history.length > 0 && (
          <div className={styles.changeLog}>
            <h3>Change log</h3>
            {bot.history.map((h, i) => {
              const before = h.from || {}, after = { ...(bot.baseTraits || {}), ...(h.to || {}) };
              const diff = Object.keys({ ...before, ...after }).filter((k) => (before[k] ?? null) !== (after[k] ?? null));
              return (
                <div key={i} className={styles.change}>
                  <div className={styles.changeHead}>
                    <strong>{new Date(h.at).toLocaleString()}</strong>
                    <span className={styles.dim}>{h.by || ""}{h.ratingAt != null ? ` · was ${h.ratingAt} after ${h.gamesAt ?? 0} games` : ""}{h.ratingReset ? " · rating reset" : ""}</span>
                  </div>
                  <div className={styles.changeDiff}>
                    {h.to == null && <span>back to default</span>}
                    {diff.map((k) => <span key={k}>{traitMeta(k).label} {before[k] ?? "unset"} → {after[k] ?? "unset"}</span>)}
                  </div>
                  {h.note && <div className={styles.changeNote}>“{h.note}”</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- the page ---------------------------------------------------------------

export default function BotLeaderboardPage() {
  const [tab, setTab] = useState<string>("all");
  const [data, setData] = useState<Record<string, BotLeaderboard | { error: string }>>({});
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "rating", dir: -1 });
  const [open, setOpen] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [reload, setReload] = useState(0);
  // removing the pre-cast bot rows: a two-step button (idle -> armed -> busy), then a result line
  const [purge, setPurge] = useState<{ step: "idle" | "armed" | "busy"; msg?: string }>({ step: "idle" });

  useEffect(() => {
    if (tab === "powerups") return; // that tab loads its own data
    let live = true;
    const level = (tab === "all" ? tab : Number(tab)) as BotLevelId;
    fetchBotLeaderboard(level).then((res) => { if (live) setData((prev) => ({ ...prev, [tab]: res })); });
    return () => { live = false; };
  }, [tab, reload]);

  // The page scrolls (SectionModal pageScroll), so the table's header row sticks
  // under whatever is pinned above it: the nav plus this section's own header
  // (title + tabs). Measured, since both change height with the window.
  const pageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const page = pageRef.current;
    const head = page?.closest(".app-modal")?.querySelector<HTMLElement>(".app-modal-head");
    if (!page || !head || typeof ResizeObserver === "undefined") return;
    const publish = () => {
      const top = getComputedStyle(head).position === "sticky" ? head.getBoundingClientRect().height + (parseFloat(getComputedStyle(head).top) || 0) : 0;
      page.style.setProperty("--table-head-top", `${Math.round(top)}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(head);
    window.addEventListener("resize", publish);
    return () => { ro.disconnect(); window.removeEventListener("resize", publish); };
  }, []);

  const res = data[tab];
  const board = res && !("error" in res) ? res : null;
  const level = tab === "all" || tab === "powerups" ? null : Number(tab);
  const ladderRow = board && level != null ? board.ladder[level] : null;
  const showLevelCol = tab === "all";
  // the traits this tab's level ignores (the header marks them; on All bots each
  // level's heading says it, since the header is shared)
  const tabIgnored = ignoredTraits(ladderRow);

  // The trait columns: every tunable trait, always (a column must not appear
  // only once somebody has tuned it), then anything else found in the pool.
  const traitKeys = useMemo(() => {
    const seen = new Set<string>();
    (board?.bots || []).forEach((b) => b.traits && Object.keys(b.traits).forEach((k) => seen.add(k)));
    return [...TRAITS.map((t) => t.key), ...[...seen].filter((k) => !TRAITS.some((t) => t.key === k))];
  }, [board]);

  // A bot's rank is its place among the bots of ITS level that have played
  // (levels are separate ladders), whatever the table is sorted by.
  const rankOf = useMemo(() => {
    const m = new Map<string, number>();
    const byLevel = new Map<string, BotRow[]>();
    (board?.bots || []).filter(played).forEach((b) => { const k = String(b.level); byLevel.set(k, [...(byLevel.get(k) || []), b]); });
    byLevel.forEach((list) => list.sort((a, b) => (b.rating! - a.rating!) || b.games - a.games).forEach((b, i) => m.set(b.agentId, i + 1)));
    return m;
  }, [board]);

  // Below the floor: a bot doing worse than its level's CrashAlot. By live rating
  // once both have played; until then by pool Elo (all a level without rated
  // matches has to go on), with a little tolerance so "roughly equal" passes.
  const belowFloor = useMemo(() => {
    const out = new Set<string>();
    const floors = new Map<number, BotRow>();
    (board?.bots || []).forEach((b) => { if (b.floor && b.level != null) floors.set(b.level, b); });
    (board?.bots || []).forEach((b) => {
      const f = b.level != null ? floors.get(b.level) : undefined;
      if (!f || b.floor) return;
      // (both need a few games first: one bad match is not a verdict)
      if (played(b) && played(f)) { if (b.games >= CORR_MIN_GAMES && f.games >= CORR_MIN_GAMES && b.rating! < f.rating! - 25) out.add(b.agentId); }
      else if (b.poolElo != null && f.poolElo != null && b.poolElo < f.poolElo - 10) out.add(b.agentId);
    });
    return out;
  }, [board]);

  // Sections: one per level on All bots, else one. Played bots first in the
  // chosen order, then the unranked ones.
  const sections = useMemo(() => {
    const q = query.trim().toLowerCase();
    const bots = (board?.bots || []).filter((b) => !q || (b.name || "").toLowerCase().includes(q) || b.poolId.toLowerCase().includes(q));
    const cmp = (a: BotRow, b: BotRow) => {
      const va = sortValue(a, sort.key), vb = sortValue(b, sort.key);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return (va - vb) * sort.dir;
    };
    const order = (list: BotRow[]) => [...list.filter(played).sort(cmp), ...list.filter((b) => !played(b)).sort((a, b) => (b.poolElo ?? 0) - (a.poolElo ?? 0))];
    if (!showLevelCol) return [{ level: level, bots: order(bots) }];
    return LEVELS.map((l) => ({ level: l.level as number | null, bots: order(bots.filter((b) => b.level === l.level)) })).filter((s) => s.bots.length);
  }, [board, sort, query, showLevelCol, level]);

  const all = board?.bots || [];
  const totals = { bots: all.length, ranked: all.filter(played).length, games: all.reduce((s, b) => s + b.games, 0), tuned: all.filter((b) => b.tuned).length };
  const cols = 8 + traitKeys.length;

  function Th({ k, children, title, off }: { k: SortKey; children: React.ReactNode; title?: string; off?: boolean }) {
    const active = sort.key === k;
    return (
      <th className={[active ? styles.thActive : "", off ? styles.thOff : ""].filter(Boolean).join(" ") || undefined} title={title} aria-sort={active ? (sort.dir === -1 ? "descending" : "ascending") : undefined}>
        <button type="button" onClick={() => setSort((p) => (p.key === k ? { key: k, dir: p.dir === -1 ? 1 : -1 } : { key: k, dir: -1 }))}>
          {children}{active ? (sort.dir === -1 ? " ▾" : " ▴") : ""}
        </button>
        {off && <span className={styles.offTag}>off</span>}
      </th>
    );
  }

  function renderRow(b: BotRow) {
    const isOpen = open === b.agentId;
    const rank = rankOf.get(b.agentId);
    const ignored = b.level != null && board ? ignoredTraits(board.ladder[b.level]) : null;
    const off = !!ignored;
    return (
      <React.Fragment key={b.agentId}>
        <tr className={[styles.row, isOpen ? styles.rowOpen : "", played(b) ? "" : styles.rowUnranked, b.floor ? styles.rowFloor : ""].filter(Boolean).join(" ")} onClick={() => setOpen(isOpen ? null : b.agentId)}>
          <td className={styles.rank}>{rank ?? ""}</td>
          <td className={styles.bot}>
            <span className={styles.botName}>{b.name || b.poolId}</span>
            {b.floor && <span className={styles.floorTag} title="The floor: the worst bot we accept on this level. Every other bot here should play at least roughly this well.">the floor</span>}
            {belowFloor.has(b.agentId) && <span className={styles.belowTag} title="Doing worse than this level's CrashAlot: a candidate for tuning or replacing.">below the floor</span>}
            {b.tuned && <span className={styles.tunedTag} title={`Settings changed by an admin (v${b.version})`}>tuned</span>}
            {!b.inPool && <span className={styles.gone}>{b.level != null ? "not in a cast" : "not in pool"}</span>}
          </td>
          <td className={styles.num}><RatedNumber value={played(b) ? b.rating : null} strong /></td>
          <td className={styles.num}>{b.poolElo == null ? null : <RatedNumber value={b.poolElo} />}</td>
          <td className={styles.num}>{b.games || <span className={styles.dim}>0</span>}</td>
          <td className={styles.num}>{cell(pct(b.wins, b.games), "%")}</td>
          <td className={styles.num}>{cell(b.placePct, "%")}</td>
          {traitKeys.map((k) => {
            const meta = traitMeta(k);
            return <td key={k} className={styles.traitCell}><TraitBar meta={meta} value={traitValueOf(b, k)} base={b.baseTraits?.[k] ?? (meta.kind === "skill" ? meta.unset : undefined)} off={!!ignored && ignored.keys.has(k)} /></td>;
          })}
          <td className={styles.chev} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
          </td>
        </tr>
        {isOpen && (
          <tr className={styles.detailRow}>
            <td colSpan={cols}>
              <BotEditor key={b.agentId + ":" + b.version} bot={b} personalitiesOff={off} onSaved={() => setReload((n) => n + 1)} />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  }

  return (
    <div className={styles.page} ref={pageRef}>
      <SectionTabs tabs={TABS} value={tab} onChange={(id) => { setTab(id); setOpen(null); }} />

      {tab === "powerups" && <PowerUpValuesTab />}
      {tab !== "powerups" && !res && <p className={styles.state}>Loading the bots…</p>}
      {res && "error" in res && (
        <p className={styles.state}>
          {res.error === "not_signed_in" ? "Sign in with an admin account to load the bots (they're hidden from everyone else)."
            : res.error === "not_admin" ? "This account isn't on the admin list."
            : res.error === "ratings_unconfigured" ? "This server has no ratings database configured (a local server?). Point the client at production to see real numbers."
            : `Couldn't load the bots (${res.error}).`}
          {" "}<button type="button" className={styles.retry} onClick={() => setReload((n) => n + 1)}>Retry</button>
        </p>
      )}

      {board && (
        <>
          <div className={styles.summary}>
            <div className={styles.stat} title={`${totals.ranked} of ${totals.bots} bots have played at least one game`}>
              <strong>{totals.ranked}<i>/{totals.bots}</i></strong><span>ranked</span>
            </div>
            <div className={styles.stat}><strong>{totals.games.toLocaleString()}</strong><span>games</span></div>
            <div className={totals.tuned ? `${styles.stat} ${styles.statTuned}` : styles.stat}><strong>{totals.tuned}</strong><span>tuned</span></div>
            <label className={styles.find}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" /></svg>
              <input type="search" value={query} placeholder="Find a bot" aria-label="Find a bot" onChange={(e) => setQuery(e.target.value)} />
            </label>
            <button type="button" className={styles.toolBtn} onClick={() => setReload((n) => n + 1)}><Icon name="refresh" size={14} /><span>Refresh</span></button>
          </div>

          {board.oldRows != null && board.oldRows > 0 && (
            <div className={styles.oldRows}>
              <p>
                <strong>{board.oldRows} old bot rating rows</strong> are still in the database, from before bots had levels. Each blends
                matches of every size, nothing writes them any more, and they still take up places on the public leaderboard.
              </p>
              {purge.step === "idle" && <button type="button" className={styles.toolBtn} onClick={() => setPurge({ step: "armed" })}>Remove them…</button>}
              {purge.step !== "idle" && (
                <span className={styles.oldRowsConfirm}>
                  <button type="button" className={styles.dangerBtn} disabled={purge.step === "busy"} onClick={async () => {
                    setPurge({ step: "busy" });
                    const r = await purgeOldBotRows();
                    setPurge({ step: "idle", msg: r.error ? `Couldn't remove them (${r.error}).` : `Removed ${r.deleted ?? "the"} old rows.` });
                    setReload((n) => n + 1);
                  }}>{purge.step === "busy" ? "Removing…" : `Yes, delete ${board.oldRows} rows for good`}</button>
                  <button type="button" className={styles.toolBtn} disabled={purge.step === "busy"} onClick={() => setPurge({ step: "idle" })}>Cancel</button>
                </span>
              )}
            </div>
          )}
          {purge.msg && <p className={styles.purgeMsg}>{purge.msg}</p>}

          {ladderRow && <LevelCard row={ladderRow} base={board.ladder[0]} boxes={board.boxes[ladderRow.level] || []} />}
          {level != null && traitKeys.length > 0 && (
            <div className={styles.corr} title={`How strongly each setting goes with a higher rating on this level, over bots with ${CORR_MIN_GAMES}+ games. Near 0 = it doesn't matter here; + = more of it ranks higher; - = less of it does.`}>
              <span className={styles.corrLabel}>What wins here</span>
              {traitKeys.map((k) => {
                const r = correlation(all, k);
                const meta = traitMeta(k);
                const off = !!tabIgnored && tabIgnored.keys.has(k);
                return (
                  <span key={k} className={`${styles.corrItem} ${off ? styles.corrOff : r == null ? "" : r > 0.15 ? styles.corrPos : r < -0.15 ? styles.corrNeg : ""}`}>
                    {meta.label} <strong>{off ? "off" : r == null ? "n/a" : (r > 0 ? "+" : "") + r.toFixed(2)}</strong>
                  </span>
                );
              })}
              {all.filter((b) => b.games >= CORR_MIN_GAMES).length < 8 && <span className={styles.dim}>needs 8 bots with {CORR_MIN_GAMES}+ games</span>}
            </div>
          )}

          {tabIgnored && (
            <p className={styles.ignoredBanner}>
              <strong>{tabIgnored.why}.</strong> {listWords([...tabIgnored.keys].map((k) => traitMeta(k).label))} do nothing here, so those columns are greyed out.
              Only planning and precision tell these bots apart.
            </p>
          )}

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th title="Place among the bots of its own level that have played">#</th>
                  <th className={styles.thBot}>Bot</th>
                  <Th k="rating" title="Live rating from real matches on its level, in its rank's colour. The result.">Rating</Th>
                  <Th k="poolElo" title="Measured offline for its default settings when the pool was built; seating picks bots by this. The prediction: over time the two columns should line up.">Pool Elo</Th>
                  <Th k="games">Games</Th>
                  <Th k="winPct">Won</Th>
                  <Th k="placePct" title="Average share of the field beaten: 1st = 100, last = 0">Beaten</Th>
                  {traitKeys.map((k) => {
                    const off = !!tabIgnored && tabIgnored.keys.has(k);
                    const label = traitMeta(k).label;
                    return <Th key={k} k={`trait:${k}`} off={off} title={(off ? `${tabIgnored!.why}, so ${label.toLowerCase()} does nothing here. ` : `${label}. `) + traitMeta(k).blurb}>{traitMeta(k).short || label}</Th>;
                  })}
                  <th />
                </tr>
              </thead>
              <tbody>
                {sections.map((s) => (
                  <React.Fragment key={String(s.level)}>
                    {showLevelCol && s.level != null && (
                      <tr className={styles.divider}>
                        <td colSpan={cols}>
                          <button type="button" onClick={() => { setTab(String(s.level)); setOpen(null); }}>
                            Level {crowdLevelNumber(s.level)} · {LEVELS[s.level].roster === "1v1" ? "1v1" : LEVELS[s.level].roster + " players"}
                          </button>
                          <span>{s.bots.filter(played).length} of {s.bots.length} ranked · {s.bots.reduce((n, b) => n + b.games, 0)} games</span>
                          {(() => {
                            const ig = board ? ignoredTraits(board.ladder[s.level]) : null;
                            return ig ? <em className={styles.ignoredNote}>{ig.why}: {listWords([...ig.keys].map((k) => traitMeta(k).label.toLowerCase()))} do nothing here</em> : null;
                          })()}
                        </td>
                      </tr>
                    )}
                    {s.bots.map(renderRow)}
                  </React.Fragment>
                ))}
                {sections.every((s) => s.bots.length === 0) && <tr><td colSpan={cols} className={styles.state}>No bot matches “{query}”.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
